import debounce from 'debounce';
import PageSkeletonLoader from '@getaccept/lib-shared-new/src/skeleton-loader/components/PageSkeletonLoader.vue';
import { FieldPlacementHelper } from '@getaccept/lib-shared-new/src/fields/helpers/field-placement.helper';
import ViewField from '@getaccept/lib-shared-new/src/document-view/components/ViewField.vue';
import EventBus from '@getaccept/lib-shared-new/src/event-bus/event-bus';
import { EventBusEvent } from '@getaccept/lib-shared-new/src/event-bus/event-bus-event';
import { computed, defineComponent, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useIntersectionObserver } from '@vueuse/core';
export default defineComponent({
    components: {
        PageSkeletonLoader,
        ViewField,
    },
    props: {
        page: {
            type: Object,
            required: true,
        },
        sender: {
            type: Object,
            default: () => ({}),
        },
        recipients: {
            type: Array,
            default: () => [],
        },
        pageWidth: {
            type: Number,
            default: 0,
        },
    },
    emits: ['visible'],
    setup(props, { emit }) {
        const { t } = useI18n();
        const imageError = ref(false);
        const imageLoaded = ref(false);
        const fields = ref([]);
        const fieldsAreConcealed = ref(false);
        const skeletonElement = ref();
        const thumbUrl = computed(() => props.page.thumbUrl);
        const skeletonHeight = computed(() => props.page ? (props.pageWidth / props.page.pageWidth) * props.page.pageHeight : 900);
        const showLoader = computed(() => !imageError.value && (!props.page.thumbUrl || !imageLoaded.value));
        const showFields = computed(() => !!imageLoaded.value && !imageError.value && !fieldsAreConcealed.value);
        const watchingPageWidth = () => {
            var _a, _b;
            if ((_b = (_a = props.page) === null || _a === void 0 ? void 0 : _a.fields) === null || _b === void 0 ? void 0 : _b.length) {
                fields.value = getFields(props.page.fields, props.pageWidth);
            }
        };
        useIntersectionObserver(skeletonElement.value, ([{ isIntersecting }]) => {
            if (!isIntersecting) {
                return;
            }
            emit('visible', props.page.id);
        }, {
            threshold: 0.1,
        });
        watch(() => props.pageWidth, watchingPageWidth);
        const concealFields = (conceal) => {
            fieldsAreConcealed.value = conceal;
        };
        const getFields = (fields, pageWidth) => fields.map((field) => (Object.assign(Object.assign({}, field), { position: FieldPlacementHelper.calculateFieldPositionForPlacement(field.position, pageWidth) })));
        const handleVisibility = (isVisible) => {
            if (!isVisible) {
                return;
            }
            emit('visible', props.page.id);
        };
        const debounceHandleVisibility = debounce(handleVisibility, 200);
        const imageDescription = (pageNumber) => t('document-page', { 'page-number': pageNumber });
        const handleImageError = () => {
            imageError.value = true;
        };
        const handleImageLoaded = () => {
            imageLoaded.value = true;
        };
        onMounted(() => {
            var _a, _b;
            if ((_b = (_a = props.page) === null || _a === void 0 ? void 0 : _a.fields) === null || _b === void 0 ? void 0 : _b.length) {
                fields.value = getFields(props.page.fields, props.pageWidth);
            }
            EventBus.on(EventBusEvent.DimensionsUpdateStarted, () => concealFields(true));
            EventBus.on(EventBusEvent.DimensionsUpdateFinished, () => concealFields(false));
        });
        return {
            t,
            skeletonHeight,
            imageLoaded,
            debounceHandleVisibility,
            showLoader,
            imageError,
            thumbUrl,
            imageDescription,
            handleImageLoaded,
            handleImageError,
            showFields,
            fields,
            handleVisibility,
            skeletonElement,
        };
    },
});
